'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { BookOpen, Clock, ShoppingCart, Check, Lock, Gift, Tag } from 'lucide-react';
import type { TestModule } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { useOwnedItems } from '@/context/OwnedItemsContext';
import { CoverPlaceholder } from '@/components/ui/CoverPlaceholder';
import { Ribbon } from '@/components/ui/Ribbon';

// Latihan tier signal (P-02) — derives the MINI/PARTISI/FULL badge straight from
// `reportType`. No parallel tier enum: two near-identical enums would drift apart.
const TIER_LABEL_KEY: Record<string, 'tierMini' | 'tierPartisi' | 'tierFull'> = {
  QUICK_PRACTICE: 'tierMini',
  SECTION_DRILL: 'tierPartisi',
  FULL_EXAM: 'tierFull',
};

function formatModuleDuration(tests: { durationMins: number }[], fallback: string): string {
  const total = tests.reduce((sum, t) => sum + (t.durationMins ?? 0), 0);
  if (!total) return fallback;
  if (total < 60) return `${total} menit`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m > 0 ? `${h} jam ${m} mnt` : `${h} jam`;
}

interface ModuleCardProps {
  module: TestModule;
  /** Show the exam-type chip. Set false when the grid is already filtered to a
   *  single exam type (the chip would just repeat the active filter). */
  showExamType?: boolean;
  /** Marks this card's cover image as the LCP candidate. Next's `priority` alone does not emit
   *  a `fetchpriority` attribute on this Next version, so both are passed explicitly. Opt-in and
   *  false by default so other catalog grids are unaffected. */
  priority?: boolean;
}

export function ModuleCard({ module, showExamType = true, priority = false }: ModuleCardProps) {
  const t = useTranslations('moduleCardCatalog');
  const { addItem, openDrawer, isInCart } = useCart();
  const { ownedModuleIds, completedModuleIds } = useOwnedItems();

  const accentColor = module.examTypes[0]?.color ?? '#0f766e';
  // module.price is the already-computed effective price (server-side
  // promo-window gated) — module.promoPrice is RAW and unconditional (not gated
  // on promoStartAt/promoEndAt). hasDiscount must compare against module.price,
  // and the price footer below must render module.price, not promoPrice
  // directly: outside the promo window the server collapses price back down to
  // basePrice, so `price < basePrice` is the only reliable "is discounted"
  // signal, and module.price is the only number that matches what checkout
  // actually charges (see CartDrawer's CartItemCard for the same pattern).
  const hasDiscount = module.promoPrice != null && module.price < module.basePrice;
  const discountPct = hasDiscount
    ? Math.round(((module.basePrice - module.price) / module.basePrice) * 100)
    : 0;

  const isOwned = ownedModuleIds.has(module.id);
  const inCart = isInCart(module.id);
  const tierLabelKey = module.reportType ? TIER_LABEL_KEY[module.reportType] : undefined;

  // Progressive unlock (P-04) — visible-but-locked, never hidden. The catalog
  // payload is publicCache(300) and identical for every viewer; the lock is
  // resolved entirely client-side against this viewer's own completed-module set.
  const isLocked = !!module.unlockAfterModuleId && !completedModuleIds.has(module.unlockAfterModuleId);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOwned || inCart || isLocked) return;
    addItem({
      type: 'module',
      moduleId: module.id,
      title: module.title,
      price: module.price,
      basePrice: module.basePrice,
      promoPrice: module.promoPrice,
      isFree: module.isFree,
      coverImageUrl: module.coverImageUrl,
    });
    openDrawer();
  };

  // Locked: rendered as a plain, non-interactive div (no Link, no cart handler
  // wired up anywhere) rather than a visually-dimmed clickable card — the
  // purchase/start action must be genuinely inert, not just styled to look inert.
  if (isLocked) {
    return (
      <div className="flex flex-col rounded-xl border border-dashed border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed overflow-hidden">
        <div className="relative aspect-video w-full overflow-hidden bg-slate-100">
          {module.coverImageUrl ? (
            <Image
              src={module.coverImageUrl}
              alt={module.title}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              className="object-cover grayscale"
              priority={priority}
              fetchPriority={priority ? 'high' : undefined}
            />
          ) : (
            <CoverPlaceholder label={module.examTypes[0]?.name ?? module.title} accentColor={module.examTypes[0]?.color ?? accentColor} />
          )}
        </div>

        <div className="p-3 flex flex-col flex-1">
          <div className="flex items-center gap-1 flex-wrap mb-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
              <Lock className="w-3 h-3" />
              {t('lockedBadge')}
            </span>
          </div>

          <h3 className="font-bold text-slate-700 text-lg leading-snug mb-2.5 line-clamp-2">
            {module.title}
          </h3>

          <p className="text-xs text-slate-500 mt-auto pt-3 border-t border-slate-200">
            {t('lockedDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    // The wrapper is not clipped, so the ribbon can overhang the card edge; the
    // Link keeps overflow-hidden for the rounded cover.
    <div className="relative h-full w-full">
    <Link
      href={`/tests/${module.slug}`}
      className="group flex flex-col h-full bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all overflow-hidden"
    >
      {/* Thumbnail — clean, no overlays */}
      <div className="relative aspect-video w-full overflow-hidden bg-slate-100">
        {module.coverImageUrl ? (
          <Image
            src={module.coverImageUrl}
            alt={module.title}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover group-hover:scale-105 transition-transform duration-300"
            priority={priority}
            fetchPriority={priority ? 'high' : undefined}
          />
        ) : (
          <CoverPlaceholder label={module.examTypes[0]?.name ?? module.title} accentColor={module.examTypes[0]?.color ?? accentColor} />
        )}
      </div>

      <div className="p-3 flex flex-col flex-1">
        {/* Badges row */}
        <div className="flex items-center gap-1 flex-wrap mb-2">
          {/* Exam-type chip — omitted when the grid is already filtered to this one
              exam type (showExamType=false); the chip would just repeat the filter. */}
          {showExamType && module.examTypes.slice(0, 1).map((et) => (
            <span
              key={et.slug}
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${et.color ?? accentColor}15`, color: et.color ?? accentColor }}
            >
              {et.name}
            </span>
          ))}
          {/* Tier badge — degrades silently when reportType is null/absent (no fabricated tier). */}
          {tierLabelKey && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
              {t(tierLabelKey)}
            </span>
          )}
        </div>

        <h3 className="font-bold text-slate-900 text-lg leading-snug mb-2.5 line-clamp-2 group-hover:text-teal-700 transition-colors">
          {module.title}
        </h3>

        {/* Meta */}
        <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
          <span className="flex items-center gap-1">
            <BookOpen className="w-3.5 h-3.5" />
            {module.tests?.length || 0} {t('ujianLabel')}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {formatModuleDuration(module.tests ?? [], t('waktuFleksibel'))}
          </span>
        </div>

        {/* Price + Cart button — price restacked (strike-through above, actual
            price below at a clearly larger size) so the number carries the weight. */}
        <div className="pt-3 border-t border-slate-100 flex items-end justify-between gap-2 mt-auto">
          {isOwned ? (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-teal-600">
              <Check className="w-3.5 h-3.5" />
              {t('dimiliki')}
            </span>
          ) : module.isFree ? (
            <span className="font-bold text-xl text-emerald-600">{t('gratis')}</span>
          ) : hasDiscount ? (
            <span className="flex flex-col">
              <span className="text-xs text-slate-400 line-through">{`Rp ${module.basePrice.toLocaleString('id-ID')}`}</span>
              <span className="font-bold text-xl text-orange-600 leading-tight">{`Rp ${module.price.toLocaleString('id-ID')}`}</span>
            </span>
          ) : (
            <span className="font-bold text-xl text-slate-900">{`Rp ${module.price.toLocaleString('id-ID')}`}</span>
          )}
          {isOwned ? null : inCart ? (
            <span className="flex items-center gap-1 text-xs text-teal-600 font-semibold min-h-[36px]">
              <Check className="w-3.5 h-3.5" />
              {t('diKeranjang')}
            </span>
          ) : (
            <button
              onClick={handleAddToCart}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 min-h-[36px] rounded-lg transition-colors ${
                module.isFree
                  ? 'bg-teal-600 hover:bg-teal-700 text-white'
                  : 'bg-orange-500 hover:bg-orange-400 text-white'
              }`}
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              {t('ikuti')}
            </button>
          )}
        </div>
      </div>
    </Link>
    {/* One ribbon at most: free wins over discount. An owned card shows no price. */}
    {!isOwned && module.isFree && <Ribbon tone="free" icon={Gift} label={t('gratis')} />}
    {!isOwned && !module.isFree && hasDiscount && (
      <Ribbon tone="discount" icon={Tag} label={`-${discountPct}%`} />
    )}
    </div>
  );
}
